import { Card, Checkbox, LearnMoreLink, Text } from '@ui/themed';
import { t } from 'i18next';
import {
  featureRestricted,
  features,
  getActiveBillingPlanKey,
} from '../../../../constants/features';
import { useEnv } from '../../../../context/env-context';
import { useSettings } from '../../../../context/settings-context';
import { SETTING_KEY_TO_LABEL } from '../../constants';
import { useCustomerGroupBuilderForm } from '../../hooks/use-customer-group-builder-form';
import {
  SettingPaymentMethodsSchema,
  type SettingPaymentMethodsState,
} from '../../types/customer-group.schema';
import { QuotingUnavailableAlert } from './quoting-unavailable-alert';
import { ResetSettingsButton } from './reset-settings-button';

type PaymentMethodKey = keyof SettingPaymentMethodsState;

export function PaymentMethodsCard() {
  const form = useCustomerGroupBuilderForm();
  const settings = useSettings();
  const env = useEnv();

  const isQuotingEngineEnabled = settings.quotingEnabled;
  const isUsingLegacyRequestForQuotingEnabled = settings.allowLegacyRequestForQuotePaymentMethod;

  const isLegacyQuotingEnabled = !isQuotingEngineEnabled && isUsingLegacyRequestForQuotingEnabled;
  const isQuotingEngineRestricted = featureRestricted(
    features.QUOTING,
    getActiveBillingPlanKey(env.billing.plan_key),
  );

  const paymentMethodKeys = Object.keys(SettingPaymentMethodsSchema.shape).filter(
    (key) => key !== 'quote' || isLegacyQuotingEnabled,
  ) as PaymentMethodKey[];

  return (
    <Card showBorder>
      <Card.Header collapsible headingLevel="h2" icon="payment">
        {SETTING_KEY_TO_LABEL.allowedPaymentMethods}
      </Card.Header>
      <Card.Body>
        <Text variant="secondary">
          {t('features:customer_group.components.payment_methods_card.description')}{' '}
          <LearnMoreLink link="https://docs.sparklayer.io/customers#_kdhT" />
        </Text>
        {paymentMethodKeys.map((key) => (
          <form.Field key={key} name={`settings.allowedPaymentMethods.${key}`}>
            {(field) => (
              <Checkbox
                label={t(
                  `features:customer_group.components.payment_methods_card.methods.${key}.label`,
                )}
                checked={!!field.state.value}
                onChange={() => field.handleChange((prev) => !prev)}
                description={t(
                  `features:customer_group.components.payment_methods_card.methods.${key}.description`,
                )}
              />
            )}
          </form.Field>
        ))}
        {isLegacyQuotingEnabled && (
          <QuotingUnavailableAlert
            isRestricted={isQuotingEngineRestricted}
            title={t(
              `features:customer_group.components.payment_methods_card.methods.quote.alert.${isQuotingEngineRestricted ? 'restricted' : 'not_enabled'}.title`,
            )}
            description={t(
              `features:customer_group.components.payment_methods_card.methods.quote.alert.${isQuotingEngineRestricted ? 'restricted' : 'not_enabled'}.description`,
            )}
          />
        )}

        <div className="flex justify-start">
          <form.Field name="settings.allowedPaymentMethods">
            {(field) => <ResetSettingsButton field={field} />}
          </form.Field>
        </div>
      </Card.Body>
    </Card>
  );
}
